<template>
  <div>
    <Loader
      v-show="loading"
      :loading="loading"
    />
    <v-card>
      <v-card-title>
        <v-row no-gutters>
          <span class="headline font-weight-medium">{{ header }}</span>
          <v-spacer />
          <IconBtn
            icons="close"
            :large="true"
            color="grey darken-4"
            @click="close"
          />
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <CustomTextField
              v-model="title"
              label="Найменування"
              :hide-details="true"
              :required="true"
              :error-messages="$v.title.$invalid ? errors.TITLE : []"
              @input="isChanged = true"
            />
          </v-col>
          <v-col>
            <EmployeeSelect
              v-model="creator"
              :multiple-select="false"
              label="Автор*"
              placeholder="Введіть iм’я співробітника"
              :error-messages="$v.creator.$invalid ? errors.IMPLEMENTER : []"
              :required="true"
              @input="isChanged = true"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <EmployeeSelect
              v-model="implementer"
              :multiple-select="false"
              label="Виконавець"
              placeholder="Введіть iм’я виконавця"
              @input="isChanged = true"
            />
          </v-col>
          <v-col>
            <DatePicker
              v-model="planDate"
              default-value
              :filled="false"
              label="Дата виконання"
              :hide-details="true"
              @input="isChanged = true"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <AgreementSelect
              v-model="agreement"
              :multiple-select="false"
              label="Угода"
              placeholder="Введіть найменування"
              :disabled="isInAgreement"
              @input="isChanged = true"
            />
          </v-col>
          <v-col>
            <v-row
              no-gutters
            >
              <v-col class="pa-2">
                <v-radio-group
                  v-model="status"
                  mandatory
                  row
                  color="#757575"
                >
                  <v-radio
                    v-for="status in statuses"
                    :key="status.value"
                    :label="status.label"
                    :value="status.value"
                  />
                </v-radio-group>
              </v-col>
              <v-col>
                <v-row no-gutters>
                  <v-col
                    v-for="type in types"
                    :key="type.value"
                  >
                    <v-btn
                      icon
                      large
                      :color="type.value == currentType ? '#e57373' : ''"
                      @click="setType(type.value)"
                    >
                      <v-icon>{{ type.icon }}</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>


        <Markdown
          :id="id"
          v-model="description"
          @input="isChanged = true"
        />
      </v-card-text>
      <v-card-actions>
        <PrimaryBtn
          :loading="loading"
          :disabled="!isChanged || isFormInvalid"
          @click="save"
        />
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import * as tasksApi from "../../backend/tasksApi.js";
import Loader from "../../../components/Loader.vue";
import IconBtn from "../../../components/IconBtn.vue";
import PrimaryBtn from "../../../components/PrimaryBtn.vue";
import CustomTextField from "../../../components/CustomTextField.vue";
import { ERRORS } from "@/helpers";
import { required } from "vuelidate/lib/validators";
import EmployeeSelect from "../employees/EmployeesSelect.vue";
import DatePicker from "@/components/story/DatePicker";
import Markdown from "../../components/Markdown.vue";
import AgreementSelect from "../agreements/AgreementSelect.vue";
import * as taskTypes from "../../helpers/enums/TaskType.js";
import * as taskStatuses from "../../helpers/enums/TaskStatus.js";

export default {
  components: {
    Loader,
    IconBtn,
    PrimaryBtn,
    CustomTextField,
    EmployeeSelect,
    DatePicker,
    Markdown,
    AgreementSelect,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    isInAgreement: {
      type: Boolean,
      default: false,
    },
    baseAgreement: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    loading: false,
    isChanged: false,
    title: "",
    creator: null,
    implementer: null,
    status: 1,
    type: 0,
    agreement: null,
    planDate: "",
    description: "",
  }),
  validations: {
    title: {
      required,
    },
    creator: {
      required,
    },
  },
  computed: {
    isFormInvalid(){
      return this.$v.title.$invalid || this.$v.creator.$invalid;
    },
    isNew() {
      return this.id != 0 ? false : true;
    },
    header() {
      return this.isNew ? "Створення завдання" : "Редагування завдання";
    },
    errors() {
      return {
        TITLE: [ERRORS.REQUIRED],
        PARTNERSHIP_STOPPED: [ERRORS.REQUIRED],
        PARTNERSHIP_STOPPED_DATE: [ERRORS.REQUIRED],
        PARTNERSHIP_STOPPED_REASON: [ERRORS.REQUIRED],
        CRM: [ERRORS.REQUIRED],
        EMAIL: [ERRORS.EMAIL],
        IMPLEMENTER: [ERRORS.REQUIRED],
      };
    },
    types(){
      return taskTypes.getTypes();
    },
    currentType(){
      return this.type;
    },
    statuses(){
      return taskStatuses.getStatuses();
    },
  },
  created() {
    if (!this.isNew) {
      tasksApi.getTask(this.id).then((task) => {
        this.title = task.title;
        this.creator = task.creator;
        this.implementer = task.implementer;
        this.status = task.status;
        this.type = task.type;
        this.planDate = task.planDate;
        this.description = task.description;
        this.agreement = task.agreement;
      });
    }
    if (this.isNew){
      this.agreement = this.baseAgreement;
    }
  },
  methods: {
    async save() {
      this.loading = true;
      try {
        const newEntity = {
          title: this.title,
          creator: this.creator?.id,
          implementer: this.implementer?.id,
          status: this.status,
          type: this.type,
          planDate: this.planDate,
          description: this.description,
          agreement: this.agreement?.id,
        };
        if (this.id == 0) {
          await tasksApi
            .createTask(newEntity)
            .then(() => {
              if (!this.isInAgreement){
                this.$router.push({ path: "/crm-tasks" });
              }
            });
        } else {
          await tasksApi.updateTask(this.id, newEntity);
          this.$notifySuccess();
        }
      } finally {
        this.loading = false;
        this.$emit("taskAdded");
      }
    },
    close() {
      if (this.isInAgreement) {
        this.$emit("close");
      } else {
        this.$router.go(-1);
      }
    },
    setType(value){
      this.type = value;
    },
  },
};
</script>